'use client';

import { useMemo } from 'react';
import { addDays } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseAsIsoDate, useQueryState } from 'nuqs';

import { PageMain } from '@/app/(app)/page-main';
import { CreateRouteDialog } from '@/app/(app)/routes/dialog';
import { RoutesTable } from '@/app/(app)/routes/table/table';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateTimeInUTC } from '@/lib/format';
import { cn } from '@/lib/utils';

export function RoutesPage() {
  const [date, setDate] = useQueryState('date', parseAsIsoDate.withDefault(new Date()));
  const formatted = useMemo(() => formatDateTimeInUTC(date, 'yyyy-MM-dd'), [date]);

  return (
    <PageMain
      title='Routes'
      right={
        <div className='flex items-center gap-4'>
          <SingleDatePicker date={date} onChange={setDate} />
          <CreateRouteDialog />
        </div>
      }
    >
      <RoutesTable date={formatted} />
    </PageMain>
  );
}

interface SingleDatePickerProps {
  date: Date;
  onChange: (date: Date) => void;
}

function SingleDatePicker({ date, onChange }: SingleDatePickerProps) {
  return (
    <div className='flex items-center gap-1'>
      <Button variant='outline' size='icon' aria-label='Previous day' onClick={() => onChange(addDays(date, -1))}>
        <ChevronLeft className='size-4' />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id='route-date'
            size='sm'
            variant='outline'
            className={cn('w-[240px] justify-start text-left font-normal')}
          >
            <CalendarIcon />
            {formatDateTimeInUTC(date, 'EEE, d MMM yyyy')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='end'>
          <Calendar
            mode='single'
            defaultMonth={date}
            selected={date}
            onSelect={(d) => d && onChange(d)}
            showOutsideDays={false}
            timeZone='UTC'
            autoFocus
          />
        </PopoverContent>
      </Popover>
      <Button variant='outline' size='icon' aria-label='Next day' onClick={() => onChange(addDays(date, 1))}>
        <ChevronRight className='h-4 w-4' />
      </Button>
    </div>
  );
}
